import { useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { UseTourGuideReturn } from "./useTourGuide";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { updateTourCompletion } from "@root/store/thunks/userProfile";

interface UseAutoTourGuideOptions {
  tourGuide: UseTourGuideReturn;
  tourCompletionFlag: string; // e.g., "legal_standard_tour_completed"
  isOnTourPath: boolean; // Whether user is currently on the tour's path
}

/**
 * Hook to handle auto-starting tour guides based on user's tour completion status
 * and marking tours as completed when they finish or are closed.
 *
 * @param tourGuide - The tour guide instance from useTourGuide
 * @param tourCompletionFlag - The user property name that tracks if tour is completed
 * @param isOnTourPath - Whether the user is currently on the tour's route
 *
 * @returns Enhanced tour guide with auto-start functionality and wrapped stopTour
 */
export const useAutoTourGuide = ({
  tourGuide,
  tourCompletionFlag,
  isOnTourPath,
}: UseAutoTourGuideOptions) => {
  const dispatch = useDispatch();
  const user: any = useSelector(getLoggedInUser);
  const hasAutoStartedRef = useRef(false);
  const hasMarkedCompletedRef = useRef(false);

  // Check if tour should be auto-started
  const shouldAutoStart =
    isOnTourPath &&
    user &&
    !user[tourCompletionFlag] &&
    !tourGuide.isActive &&
    !hasAutoStartedRef.current;

  // Auto-start tour on first visit if completion flag is false
  useEffect(() => {
    if (shouldAutoStart) {
      hasAutoStartedRef.current = true;
      tourGuide.startTour();
    }
  }, [shouldAutoStart, tourGuide]);

  // Mark tour as completed when it finishes or is closed
  const markTourCompleted = useCallback(() => {
    if (hasMarkedCompletedRef.current || !user) return;

    // Only mark as completed if the flag is currently false
    if (user[tourCompletionFlag] === false) {
      hasMarkedCompletedRef.current = true;

      dispatch(
        updateTourCompletion({
          payload: { [tourCompletionFlag]: true },
          onSuccess: () => {
            // Successfully marked as completed - Redux state will be updated automatically
          },
          onError: () => {
            // Error marking as completed - reset flag to allow retry
            hasMarkedCompletedRef.current = false;
          },
        } as any)
      );
    }
  }, [user, tourCompletionFlag, dispatch]);

  // Track when tour becomes inactive (finished or closed)
  const previousIsActiveRef = useRef<boolean>(false);
  useEffect(() => {
    // Only mark as completed if tour was active and now became inactive
    // This handles both closing mid-tour and finishing the tour
    const wasActive = previousIsActiveRef.current;
    const isActiveNow = tourGuide.isActive;

    if (wasActive && !isActiveNow && hasAutoStartedRef.current) {
      markTourCompleted();
    }

    previousIsActiveRef.current = isActiveNow;
  }, [tourGuide.isActive, markTourCompleted]);

  // Wrapped stopTour that also marks the tour as completed
  const stopTourWithCompletion = useCallback(() => {
    tourGuide.stopTour();
    // markTourCompleted will be called by the useEffect above
  }, [tourGuide]);

  // Wrapped nextStep that marks tour as completed when reaching the last step
  const nextStepWithCompletion = useCallback(() => {
    // If this is the last step, mark as completed before calling nextStep
    if (
      tourGuide.currentStep === tourGuide.totalSteps - 1 &&
      tourGuide.isActive
    ) {
      tourGuide.nextStep();
      // markTourCompleted will be called by the useEffect above when isActive becomes false
    } else {
      tourGuide.nextStep();
    }
  }, [tourGuide]);

  // Reset auto-start ref when user changes or path changes
  useEffect(() => {
    if (!isOnTourPath) {
      hasAutoStartedRef.current = false;
      hasMarkedCompletedRef.current = false;
    }
  }, [isOnTourPath]);

  return {
    ...tourGuide,
    stopTour: stopTourWithCompletion,
    nextStep: nextStepWithCompletion,
  };
};
